import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Music, Briefcase, Users, Sparkles, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Footer } from "../components/Footer";
import { trackEvent, getAbVariant } from "../lib/analytics";

const AB = {
  A: { title: <>Développez votre projet avec <span className="gold-text">Cehnura</span></>, cta: "Analyser mon projet gratuitement" },
  B: { title: <>Transformez votre projet en <span className="gold-text">plan d'action</span></>, cta: "Obtenir mon analyse gratuite" },
};

const PROFILES = [
  { icon: Music, title: "Créateur", text: "Transforme un objectif en projets et tâches, gagne de l'XP, suis ta progression et planifie tes sorties." },
  { icon: Briefcase, title: "Professionnel", text: "Publie ton profil, reçois des demandes, émets des devis et sois payé en toute sécurité après livraison." },
  { icon: Users, title: "Manager", text: "Accompagne plusieurs créateurs, suis leur activité et émets des devis en leur nom. 100% gratuit." },
];

const FEATURES = [
  "Progression, XP & niveaux gamifiés",
  "Coach IA et assistant de planification",
  "Marketplace de services avec paiement séquestre",
  "Paiements sécurisés par Stripe · sans engagement",
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const variant = getAbVariant();

  useEffect(() => {
    if (user && typeof user === "object") navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  useEffect(() => { trackEvent("landing_view", { variant }); }, [variant]);

  const goDiagnostic = () => { trackEvent("landing_cta_click", { variant }); navigate("/diagnostic"); };

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-zinc-100 flex flex-col overflow-hidden" data-testid="landing-page">
      <header className="relative z-10 border-b border-zinc-900">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={process.env.PUBLIC_URL + "/cehnura-mark.png"} alt="Cehnura" className="w-9 h-9 object-contain" />
            <span className="font-display font-extrabold text-xl">Cehn<span className="gold-text">ura</span></span>
          </div>
          <nav className="flex items-center gap-5">
            <Link to="/tarifs" className="hidden sm:inline text-sm text-zinc-400 hover:text-amber-400 transition-colors" data-testid="landing-nav-pricing">Tarifs</Link>
            <Link to="/login" className="text-sm text-zinc-400 hover:text-amber-400 transition-colors" data-testid="landing-login">Connexion</Link>
            <Link to="/register" data-testid="landing-nav-register" className="h-9 px-4 rounded-xl gold-gradient text-black text-sm font-semibold flex items-center hover:opacity-90 transition-opacity">
              Commencer
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="absolute -top-40 -left-40 w-[32rem] h-[32rem] rounded-full bg-amber-600/10 blur-3xl" />
        <div className="absolute -top-20 right-0 w-96 h-96 rounded-full bg-amber-800/10 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-4 pt-20 pb-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-amber-400/90 mb-5">
              <Sparkles className="w-4 h-4" /> Ton assistant de carrière
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] mb-5" data-testid="landing-hero-title">
              {AB[variant].title}
            </h1>
            <p className="text-base sm:text-lg text-zinc-400 mb-8 max-w-2xl">
              Cehnura t'accompagne de l'objectif à l'action : projets, progression gamifiée, coach IA, marketplace de services et mise en relation — réunis au même endroit.
            </p>
            <div className="flex flex-wrap gap-3">
              <button onClick={goDiagnostic} data-testid="landing-cta-diagnostic" className="h-12 px-7 rounded-xl gold-gradient text-black font-semibold flex items-center gap-2 hover:opacity-90 transition-opacity">
                {AB[variant].cta} <ArrowRight className="w-4 h-4" />
              </button>
              <Link to="/register" data-testid="landing-cta-register" className="h-12 px-7 rounded-xl border border-zinc-700 text-zinc-200 font-semibold flex items-center hover:bg-zinc-900 transition-colors">
                Commencer gratuitement
              </Link>
            </div>
            <p className="text-xs text-zinc-600 mt-4 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-500" /> Gratuit pour démarrer · Sans carte bancaire · Paiements sécurisés Stripe</p>
          </motion.div>
        </div>
      </section>

      {/* Profils */}
      <section className="relative border-t border-zinc-900 bg-[#0C0C10]">
        <div className="max-w-6xl mx-auto px-4 py-16">
          <h2 className="font-display text-2xl font-bold mb-2">Un espace pour chaque ambition</h2>
          <p className="text-zinc-500 mb-8 text-sm">Créateur, Professionnel ou Manager — Cehnura s'adapte à ton rôle.</p>
          <div className="grid gap-4 md:grid-cols-3">
            {PROFILES.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div key={p.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                  data-testid={`landing-profile-${i}`} className="rounded-3xl p-6 border border-zinc-800 bg-[#18181E] grain relative">
                  <div className="relative z-10">
                    <div className="w-11 h-11 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-amber-400" />
                    </div>
                    <h3 className="font-display text-xl font-bold mb-2">{p.title}</h3>
                    <p className="text-sm text-zinc-400">{p.text}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="relative">
        <div className="max-w-6xl mx-auto px-4 py-16 grid gap-10 md:grid-cols-2 items-center">
          <div>
            <h2 className="font-display text-2xl font-bold mb-4">Tout ce qu'il faut pour avancer</h2>
            <ul className="space-y-3">
              {FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-3 text-zinc-300">
                  <span className="w-5 h-5 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3.5 h-3.5 text-emerald-400" /></span>
                  {f}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl border border-amber-500/30 bg-amber-500/5 glow-gold p-8 text-center">
            <h3 className="font-display text-2xl font-bold mb-2">Prêt à lancer ta carrière ?</h3>
            <p className="text-zinc-400 text-sm mb-6">Crée ton compte en moins d'une minute et obtiens ta première recommandation.</p>
            <Link to="/register" data-testid="landing-cta-bottom" className="inline-flex h-12 px-8 rounded-xl gold-gradient text-black font-semibold items-center gap-2 hover:opacity-90 transition-opacity">
              Créer mon compte <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
